import React, { type JSX } from 'react'
import { Box, Paragraph } from '@alto-avios/alto-ui'
import { useTranslations } from 'next-intl'
import QuantityStepper from '~/components/shared/QuantityStepper'
import TravelerTypeLabel from './TravelerTypeLabel'
import { TravelerData, TravelerQuantityPickerProps, TravelersQuantity, TravelerType } from './types'
import { countTotalNumberOfTravelers } from './utils'
import styles from './TravelerQuantityPicker.module.scss'

const TravelerQuantityPicker = ({
  travelers = {},
  maxTravelers,
  maxTravelersPerRoom,
  totalTravelerCount,
  spacing,
  variant = 'success',
  onChange,
}: TravelerQuantityPickerProps): JSX.Element => {
  const t = useTranslations('components.traveller_picker.labels')
  const totalTravelers = countTotalNumberOfTravelers(travelers)

  const travelerQuantities: TravelersQuantity = Object.entries(travelers)
    .filter(([_travelerType, travelerData]) => Boolean(travelerData?.isVisible !== false))
    .reduce(
      (acc, [travelerType, travelerData]) => {
        const quantity = travelerData.quantity ?? 0
        const min = travelerData.limits?.minCount ?? 0
        const max = travelerData.limits?.maxCount ?? Number.MAX_SAFE_INTEGER

        // Hotel only
        if (
          maxTravelersPerRoom &&
          totalTravelerCount &&
          (quantity < min || quantity > maxTravelersPerRoom || totalTravelerCount > maxTravelers)
        ) {
          throw new Error(
            `Invalid number of travelers. ${maxTravelersPerRoom} travelers max per room, ${maxTravelers} max per booking. ${totalTravelerCount} > max ${maxTravelers}`
          )
        }

        // Other ancillaries
        if (
          (!maxTravelersPerRoom && (quantity < min || quantity > max)) ||
          totalTravelers > maxTravelers
        ) {
          throw new Error(`Invalid number of travelers, ${totalTravelers} > max ${max}`)
        }

        return {
          ...acc,
          [travelerType]: { quantity: travelerData.quantity },
        }
      },
      {
        adult: { quantity: 0 },
        child: { quantity: 0 },
        youth: { quantity: 0 },
        infant: { quantity: 0 },
        senior: { quantity: 0 },
        traveler: { quantity: 0 },
      }
    )

  const entries: [TravelerType, TravelerData][] = Object.values(TravelerType).map(
    (travelerType) => [travelerType, travelers[travelerType] ?? {}]
  )

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="spaceBetween"
      alignItems="center"
      columnGap="sm"
      rowGap={spacing ?? 'sm'}
    >
      {entries
        .filter(
          ([_, travelerData]) =>
            travelerData.isVisible !== false && travelerData.isVisible !== undefined
        )
        .map(([travelerType, { quantity, limits, label, ageRange }], index) => {
          const value = quantity ?? limits?.minCount ?? 0
          const min = limits?.minCount ?? 0

          const max = Math.min(
            limits?.maxCount ?? Number.MAX_SAFE_INTEGER,
            value + (maxTravelersPerRoom ?? maxTravelers) - totalTravelers
          )

          const Label = label
            ? () => label
            : () => (
                <TravelerTypeLabel
                  type={travelerType}
                  minMaxQuantity={limits}
                  ageRange={ageRange as [number, number]}
                />
              )

          return (
            <React.Fragment key={travelerType}>
              <Box
                key={travelerType}
                display="flex"
                flexDirection="column"
                gap="2xs"
                alignItems="flexStart"
                dataTestId={`traveler-quantity-picker-${travelerType}`}
                width="100%"
              >
                <Label />
                <QuantityStepper
                  className={styles.picker}
                  aria-label={travelerType}
                  defaultValue={String(value)}
                  minValue={min}
                  maxValue={max}
                  onChange={(updatedQuantity: string | number) =>
                    onChange({
                      ...travelerQuantities,
                      [travelerType]: { quantity: Number(updatedQuantity) },
                    })
                  }
                />
              </Box>

              {index === 0 && variant === 'error' && (
                <Box borderStyle="solid" borderColour="critical" borderRadius="sm" padding="xs">
                  <Paragraph size="sm" foregroundColor="criticalPrimary">
                    {t('max_infants')}
                  </Paragraph>
                </Box>
              )}
            </React.Fragment>
          )
        })}
    </Box>
  )
}

export default TravelerQuantityPicker
